import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { queryClient } from '@/lib/queryClient';
import { crossSubdomainStorage } from './crossSubdomainStorage';
import type { Tenant, Organization } from '@/types/multi-tenant';
import type { RegistrationStatus } from '@/services/auth/types';
import type { PersonaRole } from '@/data/mockEnterprise';

export interface User {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  subscriptionPlan?: string;
  /** Tenant lifecycle status from the login response: 'active' | 'suspended' | 'deleted'. */
  tenantStatus?: string;
  organizationId?: string;
  organizationName?: string;
  tenantId?: string;

  // Registration and onboarding status
  registrationStatus?: RegistrationStatus;
  onboardingProgress?: number;
  onboardingStatus?: string;
}

// Enterprise prototype: a lightweight "view as" switcher that lets the demo
// toggle between HR / estimator / designer personas without real auth.
export interface ViewAsUser {
  id: string;
  name: string;
  initials: string;
  jobTitle: string;
  teamId: string;
  email: string;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  tenant: Tenant | null;
  currentOrganization: Organization | null;
  organizations: Organization[];
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthReady: boolean;
  registrationStatus?: RegistrationStatus;
  /**
   * True once the tenant facts (plan, status) have been refreshed from the server
   * for this session. Not persisted: every page load re-syncs before plan-gated
   * routes decide, so a super-admin plan change never needs a re-login.
   */
  tenantSynced: boolean;

  // Enterprise prototype role switcher
  viewAsRole: PersonaRole;
  viewAsUser: ViewAsUser | null;

  // Actions
  setUser: (user: User | null) => void;
  setTenantSynced: (synced: boolean) => void;
  setTokens: (tokens: AuthTokens | null) => void;
  setTenant: (tenant: Tenant | null) => void;
  setCurrentOrganization: (organization: Organization | null) => void;
  setOrganizations: (organizations: Organization[]) => void;
  setLoading: (loading: boolean) => void;
  setAuthReady: (ready: boolean) => void;
  setRegistrationStatus: (status: RegistrationStatus | undefined) => void;
  login: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  switchOrganization: (organizationId: string) => Promise<void>;
  setViewAs: (role: PersonaRole, user: ViewAsUser) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tokens: null,
      tenant: null,
      tenantSynced: false,
      currentOrganization: null,
      organizations: [],
      isAuthenticated: false,
      isLoading: false,
      isAuthReady: false,
      registrationStatus: undefined,

      viewAsRole: 'hr',
      viewAsUser: null,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setTokens: (tokens) =>
        set({
          tokens,
          isAuthenticated: !!tokens,
        }),

      setTenant: (tenant) =>
        set({
          tenant,
        }),

      setCurrentOrganization: (organization) =>
        set({
          currentOrganization: organization,
        }),

      setOrganizations: (organizations) =>
        set({
          organizations,
        }),

      setLoading: (loading) =>
        set({ isLoading: loading }),

      setAuthReady: (ready) =>
        set({ isAuthReady: ready }),

      setRegistrationStatus: (status) =>
        set({ registrationStatus: status }),

      setTenantSynced: (synced) => set({ tenantSynced: synced }),

      login: (user, tokens) =>
        set({
          user,
          tokens,
          isAuthenticated: true,
          isAuthReady: true,
          isLoading: false,
          tenantSynced: false,
        }),

      logout: () => {
        // Wipe all cached server state so the previous user's data (e.g. the
        // My Learning course lists) can never be served to the next account
        // that logs in within the same tab. Without this, React Query's
        // in-memory cache survives logout and leaks across users.
        queryClient.clear();
        set({
          user: null,
          tokens: null,
          tenant: null,
          currentOrganization: null,
          organizations: [],
          isAuthenticated: false,
          tenantSynced: false,
          isAuthReady: true,
          isLoading: false,
        });
      },

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      setViewAs: (role, user) =>
        set({
          viewAsRole: role,
          viewAsUser: user,
        }),

      switchOrganization: async (organizationId: string) => {
        try {
          // Import the API function dynamically to avoid circular dependencies
          const { switchOrganization: apiSwitchOrganization } = await import('@/services/organization/api');

          // Call the API to switch organization
          await apiSwitchOrganization(organizationId);

          // Find the organization in the list
          const org = useAuthStore.getState().organizations.find(o => o.id === organizationId);

          if (org) {
            // Update the current organization
            set({
              currentOrganization: org,
            });

            // Update user's organization ID
            set((state) => ({
              user: state.user ? {
                ...state.user,
                organizationId: organizationId,
                organizationName: org.name,
              } : null,
            }));
          }
        } catch (error) {
          console.error('[AuthStore] Failed to switch organization:', error);
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      // Phase 4: persist to localStorage but mirror a trimmed session into a
      // parent-domain cookie so the learner and admin subdomains share auth
      // (silent SSO). On localhost this is plain localStorage.
      storage: createJSONStorage(() => crossSubdomainStorage),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        tenant: state.tenant,
        currentOrganization: state.currentOrganization,
        organizations: state.organizations,
        isAuthenticated: state.isAuthenticated,
        registrationStatus: state.registrationStatus,
        viewAsRole: state.viewAsRole,
        viewAsUser: state.viewAsUser,
      }),
    }
  )
);